import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { TOTAL_TOKEN_ID } from '@/shared/consts/numbers';

import { ApiPairs } from 'api';
import { Response } from 'api/apiBase/Response';
import { IPairWallet } from 'api/apiPairs/models';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { EDexPairFilters } from 'types/filters';
import { IDexPair } from 'types/pairs';
import { filtersToValues } from 'utils/filters';

const PAGE_SIZE = 100;

export const useTransferWallets = (
  pair: IDexPair,
  filtersType: EDexPairFilters = EDexPairFilters.wallets,
) => {
  const { settedPairFilters } = useFilters({
    dex: {
      pair,
      type: filtersType,
    },
  });

  const filterValues = useMemo(
    () => filtersToValues({ filters: settedPairFilters }),
    [settedPairFilters],
  );
  const filtersKey = useMemo(() => JSON.stringify(filterValues), [filterValues]);

  return useInfiniteQuery<
    Response<{ has_next: boolean; items: IPairWallet[] }>,
    unknown,
    Response<{ has_next: boolean; items: IPairWallet[] }>,
    [string, number, string, EDexPairFilters, string],
    number | undefined
  >({
    queryKey: ['pairs', pair.id, 'transfer-wallets', filtersType, filtersKey],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const wallets = filterValues.wallets?.[0] ?? undefined;
      const connectedBotsIds = filterValues.connectedBots?.[0] ?? undefined;
      const balance = filterValues.balance as {
        connection: string;
        value: string;
        tokenId: number | undefined;
      }[];
      const volume = filterValues.volume as {
        connection: string;
        value: string;
        tokenId: number | undefined;
      }[];
      const transactions_count = filterValues.transactions_count ?? undefined;
      const is_disabled = filterValues.is_disabled?.[0] ?? undefined;

      const balanceSerialized: string[] = [];
      const balanceUsdSerialized: string[] = [];

      if (balance) {
        for (const _balance of balance) {
          if (_balance.tokenId === undefined) continue;
          const value = `token${_balance.tokenId}${_balance.connection}${_balance.value}`;
          if (_balance.tokenId === TOTAL_TOKEN_ID) {
            balanceUsdSerialized.push(value);
          } else {
            balanceSerialized.push(value);
          }
        }
      }

      const volumeSerialized: string[] = [];
      const volumeUsdSerialized: string[] = [];

      if (volume) {
        for (const _volume of volume) {
          if (_volume.tokenId === undefined) continue;
          const value = `token${_volume.tokenId}${_volume.connection}${_volume.value}`;
          if (_volume.tokenId === TOTAL_TOKEN_ID) {
            volumeUsdSerialized.push(value);
          } else {
            volumeSerialized.push(value);
          }
        }
      }

      const response = await ApiPairs.getWalletsConnectedToPair({
        pairId: pair.id,
        limit: PAGE_SIZE,
        lastSeenId: pageParam,
        wallets,
        bots_ids: connectedBotsIds,
        balance: balanceSerialized.length ? balanceSerialized : undefined,
        balance_usd: balanceUsdSerialized.length ? balanceUsdSerialized : undefined,
        volume: volumeSerialized.length ? volumeSerialized : undefined,
        volume_usd: volumeUsdSerialized.length ? volumeUsdSerialized : undefined,
        transactions_count,
        is_disabled: is_disabled ?? undefined,
      });

      return response;
    },
    getNextPageParam: lastPage => {
      if (!lastPage.isSuccess || !lastPage.data?.has_next) return undefined;
      const items = lastPage.data.items ?? [];
      return items.length ? items[items.length - 1].id : undefined;
    },
  });
};
